import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axiosInstance from '../axiosInstance';
import { getPlatformBranding } from '../utils/licensingPlatformBranding';

/** @typedef {'license_tracking' | 'licensing_tool' | 'event_management'} LicensingExperienceMode */

export const LICENSING_EXPERIENCE_MODES = [
  'license_tracking',
  'licensing_tool',
  'event_management',
];

/**
 * @param {unknown} mode
 * @returns {mode is LicensingExperienceMode}
 */
export function isValidLicensingExperienceMode(mode) {
  return (
    mode === 'license_tracking' ||
    mode === 'licensing_tool' ||
    mode === 'event_management'
  );
}

const LicensingExperienceContext = createContext(null);

export function LicensingExperienceProvider({ children }) {
  const [mode, setMode] = useState(
    /** @type {LicensingExperienceMode} */ ('license_tracking')
  );
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/api/public/licensing-experience`
      );
      const m = res.data?.mode;
      if (isValidLicensingExperienceMode(m)) {
        setMode(m);
      }
    } catch {
      setMode('license_tracking');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!loading) {
      document.title = getPlatformBranding(mode).title;
    }
  }, [mode, loading]);

  const setModeAsSuperAdmin = useCallback(async (next) => {
    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_SERVER_URL}/api/superAdmins/licensing-experience`,
        { mode: next },
        { withCredentials: true }
      );
      setMode(next);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }, []);

  const value = useMemo(
    () => ({ mode, loading, refresh, setModeAsSuperAdmin }),
    [mode, loading, refresh, setModeAsSuperAdmin]
  );

  return (
    <LicensingExperienceContext.Provider value={value}>
      {children}
    </LicensingExperienceContext.Provider>
  );
}

export function useLicensingExperience() {
  const ctx = useContext(LicensingExperienceContext);
  if (!ctx) {
    throw new Error(
      'useLicensingExperience must be used within LicensingExperienceProvider'
    );
  }
  return ctx;
}

/** Safe outside provider: defaults until provider is mounted */
export function useLicensingExperienceOptional() {
  const ctx = useContext(LicensingExperienceContext);
  if (!ctx) {
    return {
      mode: /** @type {LicensingExperienceMode} */ ('license_tracking'),
      loading: true,
      refresh: async () => {},
      setModeAsSuperAdmin: async () => false,
    };
  }
  return ctx;
}

export function isLicenseTrackingMode(mode) {
  return mode === 'license_tracking';
}

export function isLicensingToolMode(mode) {
  return mode === 'licensing_tool';
}

export function isEventManagementMode(mode) {
  return mode === 'event_management';
}
